import React, { useState } from "react";
import { useLanguage } from "../../../../context/LanguageContext";
import { useAdminSettingsTranslation } from "../translations";
import { updateMultilingualShippingOptions } from "../../../../firebase/settings-functions";
import "./ShippingTab.css";

const ShippingSettings = ({ storeData, setStoreData, setError, storeId }) => {
    const [activeLanguage, setActiveLanguage] = useState('en');
    const [saving, setSaving] = useState(false);
    const [saveSuccess, setSaveSuccess] = useState("");
    const { language } = useLanguage();
    const { t } = useAdminSettingsTranslation(language);

    // Save shipping settings
    const handleSaveShipping = async () => {
        if (!storeId) {
            setError("Store ID is not configured.");
            return;
        }

        setSaving(true);
        setError("");
        setSaveSuccess("");

        try {
            await updateMultilingualShippingOptions(storeId, storeData.shippingOptions);
            setSaveSuccess(t('shippingSettingsSaved') || "Shipping settings saved successfully!");

            // Clear success message after 3 seconds
            setTimeout(() => setSaveSuccess(""), 3000);
        } catch (err) {
            setError(err.message || "Failed to save shipping settings");
        } finally {
            setSaving(false);
        }
    };

    // Local state for shipping option form
    const [shippingOption, setShippingOption] = useState({
        multilingual: {
            en: {
                name: "",
                deliveryTime: ""
            },
            ar: {
                name: "",
                deliveryTime: ""
            }
        },
        price: ""
    });

    // Handle multilingual shipping option input changes
    const handleShippingOptionChange = (e) => {
        const { name, value } = e.target;
        if (name === "price") {
            setShippingOption({
                ...shippingOption,
                price: parseFloat(value) || 0
            });
        } else {
            setShippingOption({
                ...shippingOption,
                multilingual: {
                    ...shippingOption.multilingual,
                    [activeLanguage]: {
                        ...shippingOption.multilingual[activeLanguage],
                        [name]: value
                    }
                }
            });
        }
    };

    // Add a new shipping option
    const handleAddShippingOption = (e) => {
        e.preventDefault();

        const englishName = shippingOption.multilingual.en.name;
        if (!englishName || !shippingOption.price) {
            setError(t('thisFieldIsRequired'));
            return;
        }

        const newOption = {
            id: Date.now().toString(),
            price: shippingOption.price,
            multilingual: shippingOption.multilingual,
            // Legacy fields for backward compatibility
            name: englishName,
            deliveryTime: shippingOption.multilingual.en.deliveryTime
        };

        setStoreData({
            ...storeData,
            shippingOptions: [...storeData.shippingOptions, newOption]
        });

        // Reset the form
        setShippingOption({
            multilingual: {
                en: { name: "", deliveryTime: "" },
                ar: { name: "", deliveryTime: "" }
            },
            price: ""
        });

        setError("");
    };

    // Remove a shipping option
    const handleRemoveShippingOption = (id) => {
        setStoreData({
            ...storeData,
            shippingOptions: storeData.shippingOptions.filter(option => option.id !== id)
        });
    };

    return (
        <div className="shipping-settings-section">
            <h2>{t('shippingSettings')}</h2>

            {/* Shipping Requirement Toggle */}
            <div className="shipping-requirement-setting">
                <h3>{t('shippingRequirement') || 'Shipping & Delivery'}</h3>
                <div className="form-group checkbox-group">
                    <label className="checkbox-label">
                        <input
                            type="checkbox"
                            checked={storeData.requiresShipping !== false} // Default to true for backward compatibility
                            onChange={(e) => {
                                setStoreData({
                                    ...storeData,
                                    requiresShipping: e.target.checked
                                });
                            }}
                        />
                        <span className="checkbox-text">
                            {t('requiresShipping') || 'This store requires shipping/delivery for products'}
                        </span>
                    </label>
                    <small className="help-text">
                        {t('requiresShippingHelp') || 'Uncheck this if you sell digital products, courses, or services that don\'t require physical delivery. When disabled, customers won\'t need to provide shipping addresses.'}
                    </small>
                </div>
            </div>

            {/* Show shipping options only if shipping is required */}
            {storeData.requiresShipping !== false && (
                <>
                    {/* Free Delivery Minimum Spend Setting */}
                    <div className="free-delivery-setting">
                        <h3>{t('freeDeliverySettings')}</h3>
                        <div className="form-group">
                            <label htmlFor="minimumSpend">{t('minimumSpendForFreeDelivery')}:</label>
                            <div className="minimum-spend-input">
                                <input
                                    type="number"
                                    id="minimumSpend"
                                    min="0"
                                    step="0.01"
                                    value={storeData.minimumSpendForFreeDelivery || ""}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        setStoreData(prev => ({
                                            ...prev,
                                            minimumSpendForFreeDelivery: value ? parseFloat(value) : null
                                        }));
                                    }}
                                    placeholder="Enter minimum amount (leave empty for no minimum)"
                                />
                                <span className="currency-label">{storeData.currency || 'AED'}</span>
                            </div>
                            <small className="help-text">
                                When customers spend this amount or more, shipping will be free. Leave empty to disable free delivery threshold.
                            </small>
                        </div>
                    </div>

                    {/* Language Switcher */}
                    <div className="language-switcher">
                        <label>{t('editShippingOptionsIn')}</label>
                        <div className="language-tabs">
                            <button
                                type="button"
                                className={`language-tab ${activeLanguage === 'en' ? 'active' : ''}`}
                                onClick={() => setActiveLanguage('en')}
                            >
                                {t('english')}
                            </button>
                            <button
                                type="button"
                                className={`language-tab ${activeLanguage === 'ar' ? 'active' : ''}`}
                                onClick={() => setActiveLanguage('ar')}
                            >
                                {t('arabic')}
                            </button>
                        </div>
                    </div>

                    <div className="shipping-options-container">
                        {storeData.shippingOptions.length > 0 ? (
                            <table className="admin-table shipping-table">
                                <thead>
                                    <tr>
                                        <th>{t('name')} ({activeLanguage === 'en' ? t('english') : t('arabic')})</th>
                                        <th>{t('cost')}</th>
                                        <th>{t('deliveryTime')} ({activeLanguage === 'en' ? t('english') : t('arabic')})</th>
                                        <th>{t('actions')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {storeData.shippingOptions.map(option => (
                                        <tr key={option.id}>
                                            <td dir={activeLanguage === 'ar' ? 'rtl' : 'ltr'}>
                                                {option.multilingual?.[activeLanguage]?.name || option.name || "Not set"}
                                            </td>
                                            <td>{storeData.currency} {Number(option.price).toFixed(2)}</td>
                                            <td dir={activeLanguage === 'ar' ? 'rtl' : 'ltr'}>
                                                {option.multilingual?.[activeLanguage]?.deliveryTime || option.deliveryTime || "Not specified"}
                                            </td>
                                            <td>
                                                <button
                                                    type="button"
                                                    className="shipping-remove-btn"
                                                    onClick={() => handleRemoveShippingOption(option.id)}
                                                >
                                                    {t('remove') || 'Remove'}
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        ) : (
                            <div className="no-shipping-options">
                                <p>{t('noShippingOptionsAdded')}</p>
                            </div>
                        )}
                    </div>

                    <div className="add-shipping-form">
                        <h3>{t('addShippingOption')}</h3>
                        <div className="form-row">
                            <div className="form-group">
                                <label htmlFor="shippingName">
                                    {t('name')} ({activeLanguage === 'en' ? t('english') : t('arabic')}) *
                                </label>
                                <input
                                    type="text"
                                    id="shippingName"
                                    name="name"
                                    value={shippingOption.multilingual[activeLanguage]?.name || ""}
                                    onChange={handleShippingOptionChange}
                                    placeholder={activeLanguage === 'en' ? "e.g. Standard Shipping" : "مثال: الشحن العادي"}
                                    dir={activeLanguage === 'ar' ? 'rtl' : 'ltr'}
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="shippingPrice">{t('cost')} *</label>
                                <input
                                    type="number"
                                    id="shippingPrice"
                                    name="price"
                                    value={shippingOption.price}
                                    onChange={handleShippingOptionChange}
                                    min="0"
                                    step="0.01"
                                    placeholder="0.00"
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="deliveryTime">
                                    {t('deliveryTime')} ({activeLanguage === 'en' ? t('english') : t('arabic')})
                                </label>
                                <input
                                    type="text"
                                    id="deliveryTime"
                                    name="deliveryTime"
                                    value={shippingOption.multilingual[activeLanguage]?.deliveryTime || ""}
                                    onChange={handleShippingOptionChange}
                                    placeholder={activeLanguage === 'en' ? "e.g. 3-5 business days" : "مثال: 3-5 أيام عمل"}
                                    dir={activeLanguage === 'ar' ? 'rtl' : 'ltr'}
                                />
                            </div>

                            <div className="form-group button-group">
                                <button
                                    type="button"
                                    className="add-shipping-btn"
                                    onClick={handleAddShippingOption}
                                >
                                    {t('add')}
                                </button>
                            </div>
                        </div>
                    </div>
                </>
            )}

            {/* Save Button */}
            <div className="settings-form-actions" style={{ marginTop: '20px' }}>
                {saveSuccess && <div className="admin-success-message">{saveSuccess}</div>}
                <button
                    type="button"
                    className="admin-primary-btn save-settings-btn"
                    onClick={handleSaveShipping}
                    disabled={saving}
                >
                    {saving ? (t('saving') || 'Saving...') : (t('saveShippingSettings') || 'Save Shipping Settings')}
                </button>
            </div>
        </div>
    );
};

export default ShippingSettings;